import moment from "moment";

import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RiskFactorAutocomplete, RiskFactorValue } from "@/Patient/Components/MedicalItems/RiskFactorAutoComplete";
import { MedicalItemRiskFactorHelper } from "@/Patient/Helpers/MedicalItemRiskFactorHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { BaseMedicalItemsView, IItemDetailsEditRenderFunction, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { LifeStageRow } from "./LifeStageRow";
import { RiskFactorEditList } from "./RiskFactorEditList";
import { RiskFactorInfo } from "./RiskFactorInfo";

/**
 * Auto-focuses the risk factor detail input when clicking outside of it of the risk factor autocomplete component.
 * @param target HTMLElement that was clicked
 * @param forceAutofocus If we want to bypass the check for input/select/button/textarea/a tags
 * @returns void
  */
function autoFocusRiskFactorDetailInput(target?: HTMLElement, forceAutofocus: boolean = false): void {
    try {
        // if the target is an input, select, button, textarea or anchor tag, do not focus on the risk factor input
        // this is to prevent focusing on the risk factor input when clicking outside
        if (!forceAutofocus && target && target.tagName && ["INPUT", "A", "SELECT", "BUTTON", "TEXTAREA"].includes(target.tagName?.toUpperCase()))
            return;

        // focus on the first risk factor input
        const list = document.getElementsByClassName("riskfactor-details-input");
        if (list.length > 0) {
            setTimeout(() => (list[0] as HTMLInputElement).focus?.(), 100);
        }
    }
    catch (error) {
        console.error("Error in RiskFactorAutocomplete onClickOutside: ", error);
    }
}

class MedicalItemRiskFactorViewState extends BaseMedicalItemsViewState {
    public IsNewEMRTemplate: boolean;

    public RiskFactor: RiskFactorInfo;
    public RiskFactors: RiskFactorInfo[];

    public CurrentRiskFactorIndexInBatch: number;

    public ExposureDetails: string;
    public EndDate: Date;
    public EndDateText: string;
    public EndDatePrecision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day;
    public IsEndDateApproximative: boolean;
    public Notes: string;

    public Id: string;
    public DisplayId: string;

    constructor(s: MedicalItemRiskFactorViewState = null) {
        super(s);

        if (s != null) {
            this.IsNewEMRTemplate = s.IsNewEMRTemplate;
            this.RiskFactors = s.RiskFactors;
            this.RiskFactor = s.RiskFactor;
            this.ExposureDetails = s.ExposureDetails;
            this.Notes = s.Notes;
            this.CurrentRiskFactorIndexInBatch = s.CurrentRiskFactorIndexInBatch;
            this.Id = s.Id;
            this.EndDate = s.EndDate;
            this.EndDateText = s.EndDateText;
            this.EndDatePrecision = s.EndDatePrecision;
            this.IsEndDateApproximative = s.IsEndDateApproximative;
        }
        else {
            this.RiskFactors = [];
            this.RiskFactor = { Id: null, Name: "", RiskFactorCodeId: null };
        }
    }
}

class MedicalItemsRiskFactorView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemRiskFactorViewState> {
    public componentWillMount(): void {
        var s = null;
        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null) {
            s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId);
        }
        if (s == null) {
            s = new MedicalItemRiskFactorViewState();
        }
        this.setState(s);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();
                if (latestNewItem != null) {
                    // Use the new MedicalItem
                    this.SetEditedItemState(latestNewItem);
                }
                else {
                    // modify the last item if Uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.SetEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    public render(): JSX.Element {
        var self = this;
        var state = this.state;

        let listItemRenderFunction: IListItemRenderFunction = function(riskFactor: MedicalItemRiskFactor): JSX.Element {
            var lifestageString = PatientMedicalSummaryHelper.GetLifeStageString(riskFactor);

            return (
                <div className="med-note" key={"rf" + riskFactor.Id}>
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            <p className="ellipsis bold">{MedicalItemRiskFactorHelper.getRiskFactorLabel(riskFactor, true)}</p>
                            {riskFactor.EMRTemplateIsInvalid && <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")} </p>}
                            <p className="ellipsis"> {lifestageString}</p>
                        </div>
                    </div>
                </div>
            );
        };

        let itemDetailsRenderFunction: IItemDetailsRenderFunction = function(riskFactor: MedicalItemRiskFactor): JSX.Element {
            var lifestageString = PatientMedicalSummaryHelper.GetLifeStageString(riskFactor);

            var endDateDisplay = riskFactor.EndDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(riskFactor.EndDate, riskFactor.PrecisionEndDate) : "";

            if (riskFactor.PrecisionEndDate == LifeStageDatePrecisionEnum.DayApprox && endDateDisplay != null && endDateDisplay.length > 0)
                endDateDisplay = "≈ " + endDateDisplay;

            return (
                <div key={"rf" + riskFactor.Id} className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("SocialHabitLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="socialHabit-name">{MyleHelper.ReplaceEmptyString(MedicalItemRiskFactorHelper.getRiskFactorLabel(riskFactor))}</strong>{riskFactor.Details && <span>{` - ${riskFactor.Details}`}</span>}
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ExposureDetailsDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="socialHabit-details">{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(riskFactor.ExposureDetails))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StartDateDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="socialHabit-startDate">{MyleHelper.ReplaceEmptyString(lifestageString)} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="socialHabit-endDate"> {endDateDisplay}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="socialHabit-notes">{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(riskFactor.Notes))}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        let itemDetailsEditRenderFunction: IItemDetailsEditRenderFunction = function(riskFactor: MedicalItemRiskFactor): JSX.Element {
            var isValid = false;
            if (state != null && state.IsNewEMRTemplate) {
                isValid = state.RiskFactors != null &&
                    state.RiskFactors.length > 0 &&
                    state.RiskFactors.some(x => x.Name != null && x.Name.trim().length > 0);
            }
            else {
                isValid = state.RiskFactor != null && state.RiskFactor.Name.trim().length > 0;
            }

            var endDateApproxDisabled = "";
            if (state != null)
                endDateApproxDisabled = state.EndDatePrecision == LifeStageDatePrecisionEnum.MonthYear ||
                    state.EndDatePrecision == LifeStageDatePrecisionEnum.Year ? "disabled" : "";

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            {self.state.IsNewEMRTemplate
                                ? (
                                    <>
                                        <tr>
                                            <td className="c1">
                                                <span>{cacheStore.GetString("SocialHabitLabel")}</span>
                                            </td>
                                            <td className="c2" colSpan={2}>
                                                <RiskFactorAutocomplete
                                                    valid={isValid}
                                                    onItemSelectCallback={self.RiskFactorSelected.bind(self)}
                                                    onBlurCallback={self.RiskFactorOnBlur.bind(self)}
                                                    onClickOutside={(target: HTMLElement) => autoFocusRiskFactorDetailInput(target)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="c1" />
                                            <td className="c2" colSpan={2} style={{ "textAlign": "left" }}>
                                                <RiskFactorEditList
                                                    riskFactors={self.state.RiskFactors}
                                                    onRemove={self.RemoveRiskFactorFromBatch.bind(self)}
                                                    onChangeDetails={(id, details) => self.updateDetails(id, details)}
                                                />
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td className="c1">
                                            <span>{cacheStore.GetString("SocialHabitLabel")}</span>
                                        </td>
                                        <td className="c2" colSpan={2}>
                                            {!self.state.RiskFactor.Name
                                                ? (
                                                    <RiskFactorAutocomplete
                                                        valid={isValid}
                                                        onItemSelectCallback={item => {
                                                            self.RiskFactorSelected(item);
                                                            autoFocusRiskFactorDetailInput(null, true);
                                                        }}
                                                        onBlurCallback={self.RiskFactorOnBlur.bind(self)}
                                                        onClickOutside={(target: HTMLElement) => autoFocusRiskFactorDetailInput(target)}
                                                    />
                                                ) : (
                                                    <div className="flex-display flex-align-items-center flex-gap-sm" data-cy="risk-factor-name">
                                                        <RiskFactorEditList
                                                            riskFactors={[self.state.RiskFactor]}
                                                            onRemove={() => self.setState({ RiskFactor: { Name: "", RiskFactorCodeId: null } })}
                                                            onChangeDetails={(_, details) => self.mutateState(s => s.RiskFactor.Details = details)}
                                                        />
                                                    </div>
                                                )
                                            }
                                        </td>
                                    </tr>
                                )
                            }
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ExposureDetailsDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        dataCy="risk-factor-exposure-details"
                                        id={"exposure_Det" + (riskFactor.displayId ? riskFactor.displayId : riskFactor.MedicalItemId)}
                                        text={state.ExposureDetails}
                                        isDisabled={false}
                                        onChange={value => self.ExposureDetailsChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>

                            <LifeStageRow
                                lifeStageMode={state.LifeStageMode}
                                lifeStageAvailableAgeAtOnsetUnits={state.LifeStageAvailableAgeAtOnsetUnits}
                                lifeStageAgeSelectedAgeAtOnsetUnit={state.LifeStageAgeSelectedgeAtOnsetUnit}
                                lifeStageSelectedLifeStageLookupId={state.LifeStageSelectedLifestageLookupId}
                                lifeStageAvailableLifeStages={state.LifeStageAvailableLifestages}
                                lifeStageAgeAtOnset={state.LifeStageAgeAtOnset}
                                lifeStageDatePrecision={state.LifeStageDatePrecision}
                                lifeStageDateTextboxValue={state.LifeStageDateTextboxValue}
                                lifeStageStartDate={state.LifeStageStartDate}
                                lifeStageIsStartDateApprox={state.LifeStageIsStartDateApproximative}
                                useThreeCols={false}
                                onLifeStageChange={value => self.LifeStageChanged(value)}
                                onLifeStageDateChange={(date, precision) => self.LifeStageDateChanged(date, precision)}
                                onLifeStageModeClick={newMode => self.LifeStageModeChanged(newMode)}
                                onLifeStageDateApproxChange={() => self.LifeStageDateApproxChange()}
                                onLifeStageDateTodayClick={() => self.LifeStageDateTodayClicked()}
                                onLifeStageAgeChange={value => self.LifeStageAgeChange(value)}
                                onLifeStageAgeUnitChange={value => self.LifeStageAgeUnitChange(value)}
                            />

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControlWithDatePicker
                                            id="resolutionDP"
                                            dataCy="risk-factor-end-date"
                                            textValue={self.state.EndDateText}
                                            dateValue={state.EndDate}
                                            onChange={(datePrecision: DateTimeWithPrecision) => {
                                                self.EndDateChanged(datePrecision, datePrecision.precision);
                                            }}
                                        />

                                        <div style={{ paddingLeft: "10px", marginTop: "5px" }} onClick={self.EndDateApproxChanged.bind(self).bind(self)}>
                                            <input disabled={endDateApproxDisabled === "disabled"} type="checkbox" onChange={self.EndDateApproxChanged.bind(self).bind(self)} checked={state.IsEndDateApproximative} />
                                            <span>Approx.</span>
                                        </div>
                                        <div style={{ paddingLeft: "10px", marginTop: "5px", cursor: "pointer" }}>
                                            <a style={{ cursor: "pointer" }} onClick={self.EndDateTodayClicked.bind(self)}>
                                                <span>
                                                    {cacheStore.GetString("TodayLabel")}
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"notes_Det" + (riskFactor.displayId ? riskFactor.displayId : riskFactor.MedicalItemId)}
                                        dataCy="risk-factor-details-notes"
                                        text={state.Notes}
                                        isDisabled={false}
                                        onChange={value => self.NotesChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        var self = this;
        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("SocialHabitsLabel")}
                ListItemRenderFunction={listItemRenderFunction}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                SaveFunction={self.Save.bind(self, false)}
                SavePlusFunction={self.SavePlus.bind(self)}
                emrType={MedicalItemTypeEnum.SocialHabitsAndRiskFactors}
                CheckIfSaveEnabledFunction={self.CheckIfSaveIsEnabled.bind(self)}
                toSearchableStringFunction={(items: MedicalItem[]) => self.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        let item = medItem as MedicalItemRiskFactor;
        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let isSameItem = item.displayId == this.state.DisplayId && item.Id == this.state.Id;
        if (isSameItem)
            return;

        var s = new MedicalItemRiskFactorViewState();

        s.Id = item.Id;
        s.DisplayId = item.displayId;
        if (item.IsNewEMRTemplate) {
            // we have a new medical item: contol behaves in "multiple creation mode"...
            s.RiskFactors = [];
            s.RiskFactor = { Name: "", RiskFactorCodeId: null, Details: null };
            s.ExposureDetails = "";
            s.Notes = "";
            s.EndDate = null;
            s.EndDateText = "";
            s.IsEndDateApproximative = false;
        }
        else {
            // we are modifying an already saved medical item, control behaves normally
            s.RiskFactors = [];
            s.RiskFactor = { Name: item.Name, RiskFactorCodeId: item.RiskFactorCodeId, Details: item.Details };
            s.ExposureDetails = item.ExposureDetails;
            s.EndDate = item.EndDate;
            s.EndDatePrecision = item.PrecisionEndDate;
            s.EndDateText = PatientMedicalItemsHelper.GetLifeStageDateString(s.EndDate, s.EndDatePrecision);
            s.IsEndDateApproximative = !(item.PrecisionEndDate == LifeStageDatePrecisionEnum.Day);
        }

        s.IsNewEMRTemplate = item.IsNewEMRTemplate;
        s.Notes = item.Notes;
        s.LifeStageStartDate = item.LifeStageDate;
        s.LifeStageAgeAtOnset = item.LifeStageAge;
        s.LifeStageMode = item.LifeStageMode != null ? item.LifeStageMode : s.LifeStageMode;
        s.LifeStageIsStartDateApproximative = !(item.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day);
        s.LifeStageDatePrecision = item.LifeStagePrecisionDate;
        s.LifeStageDateTextboxValue = s.LifeStageStartDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, item.LifeStagePrecisionDate) : "";

        s.LifeStageSelectedLifestageLookupId = item.LifeStageStatusLookupId != null ? item.LifeStageAgeStatusLookupId : s.LifeStageSelectedLifestageLookupId;
        s.LifeStageAgeSelectedgeAtOnsetUnit = item.LifeStageAgeStatusLookupId != null ? item.LifeStageAgeStatusLookupId : s.LifeStageAgeSelectedgeAtOnsetUnit;

        this.setState(s);
    }

    private RiskFactorSelected(item: RiskFactorValue): void {
        if (item != null) {
            const s = new MedicalItemRiskFactorViewState(this.state);

            // push item in list when in batch edit mode
            if (s.IsNewEMRTemplate) {
                const rf = new RiskFactorInfo();
                rf.Name = item.Text;
                rf.RiskFactorCodeId = item.Id;
                if (!s.RiskFactors.some(p => p.RiskFactorCodeId === item.Id))
                    s.RiskFactors.push(rf);
            }

            s.RiskFactor = { Name: item.Text, RiskFactorCodeId: item.Id };

            s.CurrentRiskFactorIndexInBatch = s.RiskFactors.length - 1;

            this.setState(s);
        }
    }

    private RiskFactorOnBlur(lastText: string): void {
        var self = this;

        if (lastText != null && lastText.trim().length > 0) {
            var s = new MedicalItemRiskFactorViewState(this.state);

            // push item in list when in batch edit mode
            if (s.IsNewEMRTemplate) {
                var rf = new RiskFactorInfo();
                rf.Name = lastText;
                s.RiskFactors.push(rf);

                s.RiskFactor = { Name: "", RiskFactorCodeId: null };
            }
            else {
                s.RiskFactor = { Name: lastText, RiskFactorCodeId: null };
            }

            self.setState(s);
        }
    }

    private ExposureDetailsChanged(value: string): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.ExposureDetails = value;
        this.setState(s);
    }

    private EndDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemRiskFactorViewState(this.state);

        if (date.value) {
            s.EndDate = moment(date.value).toDate();
            s.EndDatePrecision = precision;

            if (date.isFromDateSelector) {
                s.EndDateText = moment(date.value).format("YYYY-MM-DD");
            }
            else if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.EndDateText = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
                s.IsEndDateApproximative = false;

                if (s.LifeStageIsStartDateApproximative === false)
                    s.EndDatePrecision = LifeStageDatePrecisionEnum.Day;
            }
            else if (precision == LifeStageDatePrecisionEnum.MonthYear ||
                precision == LifeStageDatePrecisionEnum.Year) {
                s.EndDateText = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.IsEndDateApproximative = true;
            }
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.EndDate = null;
            s.EndDateText = "";
            s.IsEndDateApproximative = false;
        }

        this.setState(s);
    }

    private EndDateApproxChanged(e: React.MouseEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.IsEndDateApproximative = e.target.checked;
        s.EndDatePrecision = e.target.checked ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private EndDateTodayClicked(): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.EndDateText = moment(Date.now()).format("YYYY-MM-DD");
        s.EndDate = moment(Date.now()).toDate();
        s.EndDatePrecision = (this.state.IsEndDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private NotesChanged(value: string): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.Notes = value;
        this.setState(s);
    }

    private LifeStageModeChanged(newMode: LifeStageModeEnum): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageMode = newMode;

        if (s.LifeStageMode == LifeStageModeEnum.Age) {
            // s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.MonthYear;
            s.LifeStageAgeSelectedgeAtOnsetUnit = cacheStore.FindLookupByLookupCode("year", "DayYear").LookupID;
            s.LifeStageAgeAtOnset = undefined;
            s.LifeStageIsStartDateApproximative = false;
        }
        else if (s.LifeStageMode == LifeStageModeEnum.Date) {
            s.LifeStageIsStartDateApproximative = s.LifeStageDatePrecision != LifeStageDatePrecisionEnum.Day;
        }

        this.setState(s);
    }

    private LifeStageDateApproxChange(): void {
        if (this.state && this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.MonthYear || this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageIsStartDateApproximative = !s.LifeStageIsStartDateApproximative;
        s.LifeStageDatePrecision = s.LifeStageIsStartDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemRiskFactorViewState(this.state);

        if (date.value) {
            s.LifeStageStartDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.LifeStageDateTextboxValue = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.LifeStageDatePrecision = precision;
                s.LifeStageDateTextboxValue = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.LifeStageIsStartDateApproximative = true;
            }

            s.LifeStageMode = LifeStageModeEnum.Date;
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.LifeStageStartDate = null;
            s.LifeStageDateTextboxValue = "";
            s.LifeStageIsStartDateApproximative = false;
        }

        this.setState(s);
    }

    private LifeStageDateTodayClicked(): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageDateTextboxValue = moment(Date.now()).format("YYYY-MM-DD");
        s.LifeStageStartDate = moment(Date.now()).toDate();
        s.LifeStageMode = LifeStageModeEnum.Date;
        s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageChanged(lookupId: string): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageSelectedLifestageLookupId = lookupId;
        s.LifeStageMode = LifeStageModeEnum.LifeStage;
        this.setState(s);
    }

    private LifeStageAgeChange(value: string): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageAgeAtOnset = PatientMedicalItemsHelper.parseLifeStageAgeAtOnsetValue(value);
        s.LifeStageMode = LifeStageModeEnum.Age;

        this.setState(s);
    }

    private LifeStageAgeUnitChange(lookupId: string): void {
        var s = new MedicalItemRiskFactorViewState(this.state);
        s.LifeStageAgeSelectedgeAtOnsetUnit = lookupId;
        s.LifeStageMode = LifeStageModeEnum.Age;
        this.setState(s);
    }

    private RemoveRiskFactorFromBatch(riskFactor: RiskFactorInfo): void {
        if (!(riskFactor == null)) {
            var s = new MedicalItemRiskFactorViewState(this.state);
            s.RiskFactors = s.RiskFactors.filter(x => x.Id !== riskFactor.Id);
            this.setState(s);
        }
    }

    private updateDetails(id: string, details: string): void {
        this.mutateState(s => {
            const riskFactor = s.RiskFactors.find(x => x.Id === id);
            if (riskFactor) {
                riskFactor.Details = details;
            }
        });
    }

    private async SavePlus(): Promise<void> {
        await this.Save(true);
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        let medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem = medicalItems.firstOrDefault(x => x.IsCommitted == false) as MedicalItemRiskFactor;
        editMedicalItem = MyleHelper.CloneObject(editMedicalItem);

        let medicalItemsToSend: MedicalItemRiskFactor[] = [];

        if (editMedicalItem != null) {
            editMedicalItem.LifeStageAge = this.state.LifeStageAgeAtOnset;
            editMedicalItem.LifeStageAgeStatusLookupId = this.state.LifeStageAgeSelectedgeAtOnsetUnit;
            editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate);
            editMedicalItem.LifeStageMode = this.state.LifeStageMode;
            editMedicalItem.LifeStagePrecisionDate = this.state.LifeStageDatePrecision;
            editMedicalItem.LifeStageStatusLookupId = this.state.LifeStageSelectedLifestageLookupId;

            if (!editMedicalItem.IsNewEMRTemplate) {
                editMedicalItem.Name = this.state.RiskFactor.Name;
                editMedicalItem.RiskFactorCodeId = this.state.RiskFactor.RiskFactorCodeId;
                editMedicalItem.ExposureDetails = this.state.ExposureDetails;
                editMedicalItem.EndDate = cacheStore.Helper.CreateDateAsUTC(this.state.EndDate);
                editMedicalItem.PrecisionEndDate = this.state.EndDatePrecision;
                editMedicalItem.Notes = this.state.Notes;
                editMedicalItem.Details = this.state.RiskFactor.Details;

                medicalItemsToSend.push(editMedicalItem);
            }
            else if (this.state.RiskFactors.length > 0) {
                for (let riskFactor of this.state.RiskFactors) {
                    if (!IsNullOrWhiteSpace(riskFactor?.Name)) {
                        var batchMedicalItem: MedicalItemRiskFactor = JSON.parse(JSON.stringify(editMedicalItem));

                        batchMedicalItem.EMRTemplateId = GuidHelper.NewGuid();
                        batchMedicalItem.MedicalItemId = GuidHelper.NewGuid();
                        batchMedicalItem.StartDateLifeStageId = GuidHelper.NewGuid();

                        batchMedicalItem.Name = riskFactor.Name;
                        batchMedicalItem.RiskFactorCodeId = riskFactor.RiskFactorCodeId;
                        batchMedicalItem.ExposureDetails = this.state.ExposureDetails;
                        batchMedicalItem.EndDate = cacheStore.Helper.CreateDateAsUTC(this.state.EndDate);
                        batchMedicalItem.PrecisionEndDate = this.state.EndDatePrecision;
                        batchMedicalItem.Notes = this.state.Notes;
                        batchMedicalItem.Details = riskFactor.Details;

                        medicalItemsToSend.push(batchMedicalItem);
                    }
                }
            }
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.Save);
        operation.AddItem("view", MedicalItemsTab.RiskFactors);
        operation.AddItem("editedItems", medicalItemsToSend);
        if (isSavePlus)
            operation.AddItem("isSavePlus", true);

        LocalEventDispatcher.dispatch(operation);
    }

    private CheckIfSaveIsEnabled(): boolean {
        var isSaveEnabled = false;

        if (this.state.IsNewEMRTemplate) {
            isSaveEnabled = this.state.RiskFactors != null &&
                this.state.RiskFactors.length > 0 &&
                this.state.RiskFactors.some(x => x.Name != null && x.Name.trim().length > 0);
        }
        else {
            isSaveEnabled = this.state.RiskFactor != null && this.state.RiskFactor.Name.trim().length > 0;
        }

        return isSaveEnabled;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemRiskFactor[] {
        let self = this;
        let medicalItem: MedicalItemRiskFactor[] = items as MedicalItemRiskFactor[]; // cast MedicalItem to riskFactor

        return medicalItem.filterSearch(self.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            let startDate = MyleHelper.replaceNullWithEmptyString(item.PharmaStartDate);
            let name = MyleHelper.replaceNullWithEmptyString(item.Name);
            let lifeStageAge = MyleHelper.replaceNullWithEmptyString(item.LifeStageAge);
            let lifeStageDate = MyleHelper.replaceNullWithEmptyString(item.LifeStageDate);
            let lifeStageStatusLookup = (cacheStore.GetLookupById(item.LifeStageStatusLookupId) != null) ? cacheStore.GetLookupById(item.LifeStageStatusLookupId).GetValue() : "";

            return startDate + " " + name + " " + lifeStageAge + " " + lifeStageDate + " " + lifeStageStatusLookup;
        });
    }
}

export { MedicalItemsRiskFactorView, RiskFactorInfo };
